import Header from "./components/Header";
import Form from "./components/Form";
import InputWrapper from "../components/InputWrapper";
import {NavLink} from "react-router-dom";
import {useController, useForm} from "react-hook-form";
import {useContext, useEffect, useState} from "react";
import AppContext from "../../../context";
import Data from "../Data";
import SiteSettingsImageUpload from "./components/SiteSettingsImageUpload";
import {ChromePicker, SketchPicker} from "react-color";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Meta from "../../components/Meta";
import SiteSettingsLogoUpload from "./components/SiteSettingsLogoUpload";
import SiteSettingsFaviconUpload from "./components/SiteSettingsFaviconUpload";

const defaultPaymentState = {
    paymentProcessor: '',
    worldpayTestMode: true,
    worldpayEntity: '',
    worldpayUsername: '',
    worldpayPassword: '',
    stripeTestMode: true,
    stripeSecretKey: '',
    stripeEndpointSecret: '',
    sumupTestMode: true,
    sumupClientId: '',
    sumupClientSecret: '',
    sumupMerchantId: '',
    sumupPayToEmail: '',
    googlePayEnabled: false,
    googlePayMerchantId: '',
    applePayDomainAssociation: '',
    metaPixelEnabled: false,
    metaPixelId: '',
    metaConversionsAccessToken: '',
    metaTestEventCode: '',
    metaCurrency: 'GBP',
    remoteLockClientId: '',
    remoteLockSecret: ''
};

const SiteSettings = () => {
    const context = useContext(AppContext);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [paymentLoading, setPaymentLoading] = useState(true);
    const [paymentError, setPaymentError] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [payment, setPayment] = useState(defaultPaymentState);
    const [processorLocked, setProcessorLocked] = useState(true);
    const [remoteLockTesting, setRemoteLockTesting] = useState(false);
    const [remoteLockTestResult, setRemoteLockTestResult] = useState(null);

    const [emailLoading, setEmailLoading] = useState(true);
    const [emailError, setEmailError] = useState(false);
    const [emailSuccess, setEmailSuccess] = useState(false);
    const [emailAdmins, setEmailAdmins] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [emailRecipients, setEmailRecipients] = useState({});

    const [password, setPassword] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordError, setPasswordError] = useState(null);
    const [passwordSuccess, setPasswordSuccess] = useState(false);

    const { register, handleSubmit, formState: { errors }, reset, control } = useForm()

    const colorField = useController({
        name: "primaryColor",
        control
    })

    const termsField = useController({
        name: "terms",
        control
    })

    useEffect(() => {
        reset(context.domain)
    }, [ context.domain])

    useEffect(() => {
        const loadPaymentSettings = async () => {
            setPaymentLoading(true);
            try {
                const result = await Data.fetchDomainAdmin();
                const domain = result.data || {};
                const processor = domain.paymentProcessor || '';
                setPayment({
                    paymentProcessor: processor,
                    worldpayTestMode: domain.worldpayTestMode ?? true,
                    worldpayEntity: domain.worldpayEntity || '',
                    worldpayUsername: domain.worldpayUsername || '',
                    worldpayPassword: domain.worldpayPassword || '',
                    stripeTestMode: domain.stripeTestMode ?? true,
                    stripeSecretKey: domain.stripeSecretKey || '',
                    stripeEndpointSecret: domain.stripeEndpointSecret || '',
                    sumupTestMode: domain.sumupTestMode ?? true,
                    sumupClientId: domain.sumupClientId || '',
                    sumupClientSecret: domain.sumupClientSecret || '',
                    sumupMerchantId: domain.sumupMerchantId || '',
                    sumupPayToEmail: domain.sumupPayToEmail || '',
                    googlePayEnabled: domain.googlePayEnabled ?? false,
                    googlePayMerchantId: domain.googlePayMerchantId || '',
                    applePayDomainAssociation: domain.applePayDomainAssociation || '',
                    metaPixelEnabled: domain.metaPixelEnabled ?? false,
                    metaPixelId: domain.metaPixelId || '',
                    metaConversionsAccessToken: domain.metaConversionsAccessToken || '',
                    metaTestEventCode: domain.metaTestEventCode || '',
                    metaCurrency: domain.metaCurrency || 'GBP',
                    remoteLockClientId: domain.remoteLockClientId || '',
                    remoteLockSecret: domain.remoteLockSecret || ''
                });
                setProcessorLocked(!!processor);
                setPaymentError(false);
            } catch (err) {
                setPaymentError(true);
            } finally {
                setPaymentLoading(false);
            }
        };

        loadPaymentSettings();
    }, []);

    useEffect(() => {
        const loadEmailSettings = async () => {
            setEmailLoading(true);
            try {
                const result = await Data.fetchEmailNotificationSettings();
                const templates = result.data?.templates || [];
                setEmailAdmins(result.data?.admins || []);
                setEmailTemplates(templates);
                setEmailRecipients(
                    templates.reduce((acc, template) => {
                        acc[template.key] = template.recipientIds || [];
                        return acc;
                    }, {})
                );
                setEmailError(false);
            } catch (err) {
                setEmailError(true);
            } finally {
                setEmailLoading(false);
            }
        };

        loadEmailSettings();
    }, []);

    const toggleEmailRecipient = (templateKey, adminId) => {
        setEmailRecipients((prev) => {
            const current = prev[templateKey] || [];
            const next = current.includes(adminId)
                ? current.filter((id) => id !== adminId)
                : [...current, adminId];
            return { ...prev, [templateKey]: next };
        });
    };

    const submitEmail = async (event) => {
        event.preventDefault();
        setEmailLoading(true);
        setEmailError(false);
        try {
            const result = await Data.updateEmailNotificationSettings(emailRecipients);
            const templates = result.data?.templates || [];
            setEmailTemplates(templates);
            setEmailRecipients(
                templates.reduce((acc, template) => {
                    acc[template.key] = template.recipientIds || [];
                    return acc;
                }, {})
            );
            setEmailSuccess(true);
            setTimeout(() => setEmailSuccess(false), 3000);
        } catch (err) {
            setEmailError(true);
        } finally {
            setEmailLoading(false);
        }
    }

    const submit = async (data) => {
        try {
            await Data.updateSiteSettings(data)
            context.fetchDomain()
            setError(false)
            setSuccess(true)
        } catch (error) {
            setError(true)
        } finally {
            setLoading(false)
        }
    }

    const submitPayment = async (event) => {
        event.preventDefault();
        setPaymentLoading(true);
        setPaymentError(false);
        try {
            const updatePayload = {
                worldpayTestMode: payment.worldpayTestMode,
                worldpayEntity: payment.worldpayEntity,
                worldpayUsername: payment.worldpayUsername,
                worldpayPassword: payment.worldpayPassword,
                stripeTestMode: payment.stripeTestMode,
                stripeSecretKey: payment.stripeSecretKey,
                stripeEndpointSecret: payment.stripeEndpointSecret,
                sumupTestMode: payment.sumupTestMode,
                sumupClientId: payment.sumupClientId,
                sumupClientSecret: payment.sumupClientSecret,
                sumupMerchantId: payment.sumupMerchantId,
                sumupPayToEmail: payment.sumupPayToEmail,
                googlePayEnabled: payment.googlePayEnabled,
                googlePayMerchantId: payment.googlePayMerchantId,
                applePayDomainAssociation: payment.applePayDomainAssociation,
                metaPixelEnabled: payment.metaPixelEnabled,
                metaPixelId: payment.metaPixelId,
                metaConversionsAccessToken: payment.metaConversionsAccessToken,
                metaTestEventCode: payment.metaTestEventCode,
                metaCurrency: payment.metaCurrency || 'GBP',
                remoteLockClientId: payment.remoteLockClientId,
                remoteLockSecret: payment.remoteLockSecret
            };

            if (!processorLocked && payment.paymentProcessor) {
                updatePayload.paymentProcessor = payment.paymentProcessor;
            }

            await Data.updateSiteSettings(updatePayload);
            context.fetchDomain();
            if (!processorLocked && payment.paymentProcessor) {
                setProcessorLocked(true);
            }
            setPaymentSuccess(true);
            setTimeout(() => setPaymentSuccess(false), 3000);
        } catch (err) {
            setPaymentError(true);
        } finally {
            setPaymentLoading(false);
        }
    }

    const handleRemoteLockTestConnection = async () => {
        setRemoteLockTesting(true);
        setRemoteLockTestResult(null);
        try {
            const result = await Data.testRemoteLockConnection({
                remoteLockClientId: payment.remoteLockClientId,
                remoteLockSecret: payment.remoteLockSecret
            });
            setRemoteLockTestResult({ success: true, message: result.data?.message || 'Successfully connected to RemoteLock.' });
        } catch (err) {
            setRemoteLockTestResult({ success: false, message: err.response?.data?.message || 'Failed to connect to RemoteLock.' });
        } finally {
            setRemoteLockTesting(false);
        }
    }

    const submitPassword = async (event) => {
        event.preventDefault();
        setPasswordError(null);
        setPasswordSuccess(false);

        if (!password.currentPassword || !password.newPassword) {
            setPasswordError('Please fill in all password fields.');
            return;
        }
        if (password.newPassword !== password.confirmPassword) {
            setPasswordError('New password and confirmation do not match.');
            return;
        }

        setPasswordLoading(true);
        try {
            await Data.changePassword(context.user?.email, password.currentPassword, password.newPassword);
            setPassword({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setPasswordSuccess(true);
            setTimeout(() => setPasswordSuccess(false), 3000);
        } catch (err) {
            setPasswordError(err.response?.data?.message || 'Failed to update password.');
        } finally {
            setPasswordLoading(false);
        }
    }

    return (
        <>
            <Meta title={"Site Settings"} />
            <Header heading={"Site Settings"} />
            <div className={"p-3 lg:p-6 grid grid-cols-1 gap-3 lg:gap-6 w-full overflow-x-scroll"}>

                <Form heading={"Payment Gateway"} onSubmit={submitPayment}>
                    {paymentLoading ? (
                        <p className={"text-textGrey"}>Loading payment settings...</p>
                    ) : (
                        <>
                            <InputWrapper label={"Payment Processor"} input={
                                processorLocked ? (
                                    <p>{payment.paymentProcessor || '-'}</p>
                                ) : (
                                    <select
                                        value={payment.paymentProcessor}
                                        onChange={(event) => setPayment({ ...payment, paymentProcessor: event.target.value })}
                                    >
                                        <option value="">Select a processor</option>
                                        <option value="Worldpay">Worldpay</option>
                                        <option value="Stripe">Stripe</option>
                                        <option value="SumUp">SumUp</option>
                                    </select>
                                )
                            } />

                            {payment.paymentProcessor === 'Worldpay' && (
                                <>
                                    <InputWrapper label={"Worldpay Test Mode"} input={
                                        <select
                                            value={payment.worldpayTestMode ? 'true' : 'false'}
                                            onChange={(event) => setPayment({ ...payment, worldpayTestMode: event.target.value === 'true' })}
                                        >
                                            <option value="true">Enabled</option>
                                            <option value="false">Disabled</option>
                                        </select>
                                    } />
                                    <InputWrapper label={"Worldpay Entity"} input={
                                        <input
                                            type={"text"}
                                            value={payment.worldpayEntity}
                                            onChange={(event) => setPayment({ ...payment, worldpayEntity: event.target.value })}
                                        />
                                    } />
                                    <InputWrapper label={"Worldpay Username"} input={
                                        <input
                                            type={"text"}
                                            value={payment.worldpayUsername}
                                            onChange={(event) => setPayment({ ...payment, worldpayUsername: event.target.value })}
                                        />
                                    } />
                                    <InputWrapper label={"Worldpay Password"} input={
                                        <input
                                            type={"password"}
                                            value={payment.worldpayPassword}
                                            onChange={(event) => setPayment({ ...payment, worldpayPassword: event.target.value })}
                                        />
                                    } />
                                </>
                            )}

                            {payment.paymentProcessor === 'Stripe' && (
                                <>
                                    <InputWrapper label={"Stripe Test Mode"} input={
                                        <select
                                            value={payment.stripeTestMode ? 'true' : 'false'}
                                            onChange={(event) => setPayment({ ...payment, stripeTestMode: event.target.value === 'true' })}
                                        >
                                            <option value="true">Enabled</option>
                                            <option value="false">Disabled</option>
                                        </select>
                                    } />
                                    <InputWrapper label={"Stripe Secret Key"} input={
                                        <input
                                            type={"password"}
                                            value={payment.stripeSecretKey}
                                            onChange={(event) => setPayment({ ...payment, stripeSecretKey: event.target.value })}
                                        />
                                    } />
                                    <InputWrapper label={"Stripe Endpoint Secret"} input={
                                        <input
                                            type={"password"}
                                            value={payment.stripeEndpointSecret}
                                            onChange={(event) => setPayment({ ...payment, stripeEndpointSecret: event.target.value })}
                                        />
                                    } />
                                </>
                            )}

                            {payment.paymentProcessor === 'SumUp' && (
                                <>
                                    <InputWrapper label={"SumUp Test Mode"} input={
                                        <select
                                            value={payment.sumupTestMode ? 'true' : 'false'}
                                            onChange={(event) => setPayment({ ...payment, sumupTestMode: event.target.value === 'true' })}
                                        >
                                            <option value="true">Enabled</option>
                                            <option value="false">Disabled</option>
                                        </select>
                                    } />
                                    <InputWrapper label={"SumUp Client ID"} input={
                                        <input
                                            type={"text"}
                                            value={payment.sumupClientId}
                                            onChange={(event) => setPayment({ ...payment, sumupClientId: event.target.value })}
                                        />
                                    } />
                                    <InputWrapper label={"SumUp Client Secret"} input={
                                        <input
                                            type={"password"}
                                            value={payment.sumupClientSecret}
                                            onChange={(event) => setPayment({ ...payment, sumupClientSecret: event.target.value })}
                                        />
                                    } />
                                    <InputWrapper label={"SumUp Merchant ID"} input={
                                        <input
                                            type={"text"}
                                            value={payment.sumupMerchantId}
                                            onChange={(event) => setPayment({ ...payment, sumupMerchantId: event.target.value })}
                                        />
                                    } />
                                    <InputWrapper label={"SumUp Pay To Email"} input={
                                        <input
                                            type={"email"}
                                            value={payment.sumupPayToEmail}
                                            onChange={(event) => setPayment({ ...payment, sumupPayToEmail: event.target.value })}
                                        />
                                    } />
                                    <InputWrapper label={"Apple Pay Domain Association File"} input={
                                        <textarea
                                            rows={4}
                                            placeholder={"Paste the contents of this domain's apple-developer-merchantid-domain-association file, issued by SumUp for this exact hostname."}
                                            value={payment.applePayDomainAssociation}
                                            onChange={(event) => setPayment({ ...payment, applePayDomainAssociation: event.target.value })}
                                        />
                                    } />
                                    <InputWrapper label={"Google Pay"} input={
                                        <select
                                            value={payment.googlePayEnabled ? 'true' : 'false'}
                                            onChange={(event) => setPayment({ ...payment, googlePayEnabled: event.target.value === 'true' })}
                                        >
                                            <option value="false">Disabled</option>
                                            <option value="true">Enabled</option>
                                        </select>
                                    } />
                                    {payment.googlePayEnabled && (
                                        <InputWrapper label={"Google Pay Merchant ID"} input={
                                            <input
                                                type={"text"}
                                                value={payment.googlePayMerchantId}
                                                onChange={(event) => setPayment({ ...payment, googlePayMerchantId: event.target.value })}
                                            />
                                        } />
                                    )}
                                </>
                            )}

                            <div className={"border-t pt-4 mt-4"}>
                                <p className={"text-lg mb-2"}>Meta Pixel</p>
                                <InputWrapper label={"Enable Meta Pixel"} input={
                                    <select
                                        value={payment.metaPixelEnabled ? 'true' : 'false'}
                                        onChange={(event) => setPayment({ ...payment, metaPixelEnabled: event.target.value === 'true' })}
                                    >
                                        <option value="false">Disabled</option>
                                        <option value="true">Enabled</option>
                                    </select>
                                } />
                                <InputWrapper label={"Meta Pixel ID"} input={
                                    <input
                                        type={"text"}
                                        value={payment.metaPixelId}
                                        onChange={(event) => setPayment({ ...payment, metaPixelId: event.target.value })}
                                    />
                                } />
                                <InputWrapper label={"Conversions API Token"} input={
                                    <input
                                        type={"password"}
                                        value={payment.metaConversionsAccessToken}
                                        onChange={(event) => setPayment({ ...payment, metaConversionsAccessToken: event.target.value })}
                                    />
                                } />
                                <InputWrapper label={"Test Event Code"} input={
                                    <input
                                        type={"text"}
                                        value={payment.metaTestEventCode}
                                        onChange={(event) => setPayment({ ...payment, metaTestEventCode: event.target.value })}
                                    />
                                } />
                                <InputWrapper label={"Meta Currency"} input={
                                    <input
                                        type={"text"}
                                        value={payment.metaCurrency}
                                        onChange={(event) => setPayment({ ...payment, metaCurrency: event.target.value.toUpperCase() })}
                                        maxLength={3}
                                    />
                                } />
                            </div>

                            <div className={"border-t pt-4 mt-4"}>
                                <p className={"text-lg mb-2"}>RemoteLock Access Control</p>
                                <InputWrapper label={"RemoteLock Client ID"} input={
                                    <input
                                        type={"text"}
                                        value={payment.remoteLockClientId}
                                        onChange={(event) => setPayment({ ...payment, remoteLockClientId: event.target.value })}
                                    />
                                } />
                                <InputWrapper label={"RemoteLock Secret"} input={
                                    <input
                                        type={"password"}
                                        value={payment.remoteLockSecret}
                                        onChange={(event) => setPayment({ ...payment, remoteLockSecret: event.target.value })}
                                    />
                                } />
                                <InputWrapper label={"Connection"} input={
                                    <div>
                                        <button
                                            type="button"
                                            disabled={remoteLockTesting || !payment.remoteLockClientId || !payment.remoteLockSecret}
                                            onClick={handleRemoteLockTestConnection}
                                            className={"bg-backgroundGrey border px-2.5 py-1.5 rounded-md hover:opacity-80 transition disabled:opacity-50"}
                                        >
                                            {remoteLockTesting ? 'Testing...' : 'Test Connection'}
                                        </button>
                                        {remoteLockTestResult && (
                                            <p className={remoteLockTestResult.success ? "text-green-600 text-sm mt-2" : "text-red-500 text-sm mt-2"}>
                                                {remoteLockTestResult.message}
                                            </p>
                                        )}
                                    </div>
                                } />
                            </div>

                            {paymentError ? <p className={"text-red-500 text-sm"}>Failed to load or update payment settings.</p> : null}
                            {paymentSuccess ? <p className={"text-green-600 text-sm"}>Payment settings updated successfully.</p> : null}

                            <div className={"mt-2 flex gap-2"}>
                                <button type={"submit"} disabled={paymentLoading}
                                        className={"bg-backgroundGrey border px-2.5 py-1.5 rounded-md hover:opacity-80 transition"}>{paymentLoading ? "Saving..." : "Save Payment Settings"}</button>
                            </div>
                        </>
                    )}
                </Form>

                <Form heading={"Settings"} onSubmit={handleSubmit(submit)}>
                    <InputWrapper label={"Business Name"}
                                  input={<input type={"text"} {...register("name", {required: true})} />}
                                  error={errors.name ? "This is a required field" : null}/>
                    <InputWrapper label={"Heading"}
                                  input={<input type={"text"} {...register("heading", {required: true})} />}
                                  error={errors.heading ? "This is a required field" : null}/>
                    <InputWrapper label={"Sub Heading"}
                                  input={<input type={"text"} {...register("subHeading", {required: true})} />}
                                  error={errors.subHeading ? "This is a required field" : null}/>
                    <InputWrapper label={"Primary Color"} input={
                        <ChromePicker color={colorField.field.value}
                                      onChange={(color, event) => colorField.field.onChange(color.hex)}/>
                    } error={errors.primaryColor ? "This is a required field" : null}/>
                    <InputWrapper label={"Contact Phone Number"}
                                  input={<input type={"text"} {...register("contactPhone", {required: false})} />}
                    />
                    <InputWrapper label={"Contact Email Address"}
                                  input={<input type={"text"} {...register("contactEmail", {required: false})} />}
                    />
                    <InputWrapper label={"Auto-Accept Bookings"} input={
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                {...register("autoAcceptBooking")}
                                className="w-4 h-4"
                            />
                            <span className="text-sm text-gray-600">
                                Automatically confirm bookings upon creation (recommended for clinic, salon, restaurant)
                            </span>
                        </div>
                    } />
                    <InputWrapper label={"Booking Email Notifications"} input={
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                {...register("notifyAdminOnExperienceBooking")}
                                className="w-4 h-4"
                            />
                            <span className="text-sm text-gray-600">
                                Send a copy of the booking confirmation email to your admin email address whenever a customer makes a booking.
                            </span>
                        </div>
                    } />
                    <InputWrapper label={"Enable Wallet"} input={
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                {...register("walletEnabled")}
                                className="w-4 h-4"
                            />
                            <span className="text-sm text-gray-600">
                                Let customers buy wallet credit and spend it on bookings.
                            </span>
                        </div>
                    } />
                    <InputWrapper label={"Health Screening"} input={
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                {...register("healthScreeningEnabled")}
                                className="w-4 h-4"
                            />
                            <span className="text-sm text-gray-600">
                                Require customers to complete a health screening form before they can make a booking.
                            </span>
                        </div>
                    } />
                    <InputWrapper label={"Low Balance Threshold"} input={
                        <input type={"number"} {...register("walletLowBalanceThreshold", { required: false, valueAsNumber: true })} />
                    } />
                    <InputWrapper label={"Low Balance Reminders"} input={
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                {...register("walletLowBalanceRemindersEnabled")}
                                className="w-4 h-4"
                            />
                            <span className="text-sm text-gray-600">
                                Email customers when their wallet drops below the threshold.
                            </span>
                        </div>
                    } />

                    <div className={"border-t pt-4 mt-4"}>
                        <p className={"text-lg mb-2"}>Terms & Conditions</p>
                        <InputWrapper label={"Terms Document Name"}
                                      input={<input type={"text"} {...register("termsDocumentName")} />}
                        />
                        <InputWrapper label={"Terms"} input={
                            <div className={"terms-quill-editor relative z-10"}>
                                <ReactQuill theme="snow" value={termsField.field.value || ''}
                                            onChange={(value) => termsField.field.onChange(value)} />
                            </div>
                        } />
                        <InputWrapper label={"When should customers see the T&C?"} input={
                            <select {...register("termsAcceptanceMethod")}>
                                <option value="signup">Require confirmation when creating an account</option>
                                <option value="email">Include in the booking confirmation email</option>
                            </select>
                        } />
                    </div>

                    <div className={"mt-2 flex gap-2"}>
                        <button type={"submit"} disabled={loading}
                                className={"bg-backgroundGrey border px-2.5 py-1.5 rounded-md hover:opacity-80 transition"}>{success ? 'Saved' : loading ? "Saving..." : "Save Changes"}</button>
                    </div>
                </Form>

                <style>{`
                    .terms-quill-editor .ql-picker-options { z-index: 20; }
                    .terms-quill-editor .ql-tooltip { z-index: 20; }
                `}</style>

                <Form heading={"Emails"} onSubmit={submitEmail}>
                    {emailLoading ? (
                        <p className={"text-textGrey"}>Loading email settings...</p>
                    ) : (
                        <>
                            <p className={"text-textGrey text-sm"}>Choose which admin users receive each notification email. If no admin users are selected for a template, it falls back to the site's admin email.</p>

                            {emailAdmins.length === 0 && (
                                <p className={"text-red-500 text-sm"}>No admin users found for this site. Add an admin user under Users before assigning recipients here.</p>
                            )}

                            {emailTemplates.map((template) => (
                                <div key={template.key} className={"border rounded-md p-3"}>
                                    <p className={"font-medium"}>{template.name}</p>
                                    <p className={"text-textGrey text-sm mb-2"}>{template.description}</p>

                                    <div className={"grid grid-cols-1 gap-1"}>
                                        {emailAdmins.map((admin) => (
                                            <label key={admin._id} className={"flex items-center gap-2"}>
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4"
                                                    checked={(emailRecipients[template.key] || []).includes(admin._id)}
                                                    onChange={() => toggleEmailRecipient(template.key, admin._id)}
                                                />
                                                <span className={"text-sm text-gray-600"}>{admin.firstName} {admin.lastName} ({admin.email})</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            {emailError ? <p className={"text-red-500 text-sm"}>Failed to load or update email notification settings.</p> : null}
                            {emailSuccess ? <p className={"text-green-600 text-sm"}>Email notification settings updated successfully.</p> : null}

                            <div className={"mt-2 flex gap-2"}>
                                <button type={"submit"} disabled={emailLoading}
                                        className={"bg-backgroundGrey border px-2.5 py-1.5 rounded-md hover:opacity-80 transition"}>{emailLoading ? "Saving..." : "Save Email Settings"}</button>
                            </div>
                        </>
                    )}
                </Form>

                <Form heading={"Change Password"} onSubmit={submitPassword}>
                    <InputWrapper label={"Current Password"} input={
                        <input type={"password"} value={password.currentPassword}
                               onChange={(event) => setPassword({ ...password, currentPassword: event.target.value })} />
                    } />
                    <InputWrapper label={"New Password"} input={
                        <input type={"password"} value={password.newPassword}
                               onChange={(event) => setPassword({ ...password, newPassword: event.target.value })} />
                    } />
                    <InputWrapper label={"Confirm New Password"} input={
                        <input type={"password"} value={password.confirmPassword}
                               onChange={(event) => setPassword({ ...password, confirmPassword: event.target.value })} />
                    } />

                    {passwordError ? <p className={"text-red-500 text-sm"}>{passwordError}</p> : null}
                    {passwordSuccess ? <p className={"text-green-600 text-sm"}>Password updated successfully.</p> : null}

                    <div className={"mt-2 flex gap-2"}>
                        <button type={"submit"} disabled={passwordLoading}
                                className={"bg-backgroundGrey border px-2.5 py-1.5 rounded-md hover:opacity-80 transition"}>{passwordLoading ? "Saving..." : "Change Password"}</button>
                    </div>
                </Form>

                <SiteSettingsImageUpload/>

                <SiteSettingsLogoUpload/>

                <SiteSettingsFaviconUpload/>

            </div>
        </>
    )
}

export default SiteSettings
